import { theme } from 'antd';
import type { GlobalToken } from 'antd/es/theme/interface';
import { useLayoutEffect } from 'react';

type TokenKey = keyof GlobalToken;

/**
 * Variables `--ipb-*` publicadas en `:root` → token de antd (ya calculado por el algoritmo claro/oscuro).
 * Las consumen tailwind-theme.css, shadcn-theme.css y chat/chat-theme.css.
 * Para exponer un token nuevo, se agrega aquí y se documenta en src/styles/CLAUDE.md.
 */
const colorVars: Record<string, TokenKey> = {
  // Marca
  '--ipb-color-primary': 'colorPrimary',
  '--ipb-color-primary-hover': 'colorPrimaryHover',
  '--ipb-color-primary-active': 'colorPrimaryActive',
  '--ipb-color-primary-bg': 'colorPrimaryBg',
  '--ipb-color-primary-bg-hover': 'colorPrimaryBgHover',
  '--ipb-color-primary-border': 'colorPrimaryBorder',
  '--ipb-color-primary-text': 'colorPrimaryText',
  '--ipb-color-on-primary': 'colorTextLightSolid',
  // Fondos
  '--ipb-color-bg-container': 'colorBgContainer',
  '--ipb-color-bg-elevated': 'colorBgElevated',
  '--ipb-color-bg-layout': 'colorBgLayout',
  '--ipb-color-fill': 'colorFill',
  '--ipb-color-fill-secondary': 'colorFillSecondary',
  '--ipb-color-fill-tertiary': 'colorFillTertiary',
  // Texto
  '--ipb-color-text': 'colorText',
  '--ipb-color-text-secondary': 'colorTextSecondary',
  '--ipb-color-text-tertiary': 'colorTextTertiary',
  '--ipb-color-text-disabled': 'colorTextDisabled',
  // Bordes
  '--ipb-color-border': 'colorBorder',
  '--ipb-color-border-secondary': 'colorBorderSecondary',
  // Estados
  '--ipb-color-success': 'colorSuccess',
  '--ipb-color-warning': 'colorWarning',
  '--ipb-color-error': 'colorError',
  '--ipb-color-info': 'colorInfo',
  '--ipb-color-link': 'colorLink',
};

/** Tokens numéricos (px). */
const sizeVars: Record<string, TokenKey> = {
  '--ipb-radius-sm': 'borderRadiusSM',
  '--ipb-radius': 'borderRadius',
  '--ipb-radius-lg': 'borderRadiusLG',
};

/**
 * Publica los tokens de antd como variables CSS `--ipb-*` en `:root`.
 * Va dentro del `ConfigProvider` (src/App.tsx); se actualiza al cambiar el tema (`isDark`).
 */
export function ThemeBridge() {
  const { token } = theme.useToken();

  useLayoutEffect(() => {
    const root = document.documentElement.style;
    for (const [name, key] of Object.entries(colorVars)) root.setProperty(name, String(token[key]));
    for (const [name, key] of Object.entries(sizeVars)) root.setProperty(name, `${token[key]}px`);
  }, [token]);

  return null;
}
